{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2018 - 2020                               }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.JSON;

interface

uses
  Classes, Web, JS, SysUtils;

type
  TJSONObject = class;
  TJSONString = class;

  TJSONAncestor = class(TObject)
  private
  protected
    function GetStrValue: string; virtual;
    procedure SetStrValue(const Value: string); virtual;
  public
    property Value: string read GetStrValue write SetStrValue;
  end;

  TJSONValue = class(TJSONAncestor)
  private
    fjv: JSValue;
  protected
    property jv: JSValue read fjv write fjv;
  public
    constructor Create(AJSValue: JSValue); overload;
    function ToString: string; override;
  end;

  TJSONPair = class(TObject)
  private
    fjv: TJSONValue;
    fjs: TJSONString;
  public
    constructor Create; overload;
    constructor Create(const Str: string; const Value: TJSONValue); overload;
    constructor Create(const Str: string; const Value: string); overload;
    destructor Destroy; override;
    property JsonValue: TJSONValue read fjv write fjv;
    property JsonString: TJSONString read fjs write fjs;
    function ToString: string; override;
  end;

  TJSONPairList = class(TList);

  TJSONObject = class(TJSONValue)
  private
    fjo: TJSObject;
    FMembers: TJSONPairList;
    function GetPair(const I: Integer): TJSONPair;
    function GetCount: Integer;
    function GetItem(const Index: Integer): TJSONValue;
  protected
    property jo: TJSObject read fjo write fjo;
    procedure AddDescendant(const Descendent: TJSONPair); virtual;
    function JSONValueFromJS(const AJSValue: JSValue): TJSONValue;
  public
    constructor Create; overload;
    constructor Create(AObject: TJSObject); overload;
    destructor Destroy; override;
    property JSObject: TJSObject read fjo;
    class function ParseJSONValue(const data: string): TJSONValue;
    function GetJSONValue(Name: string): string;
    function Get(Name: string): TJSONPair; overload;
    function Get(Index: Integer): TJSONPair; overload;
    function GetValue(const Name: string): TJSONValue;
    property Items[const Index: Integer]: TJSONValue read GetItem;
    property Values[const Name: string]: TJSONValue read GetValue;
    property Pairs[const Index: Integer]: TJSONPair read GetPair;
    property Count: Integer read GetCount;
    function AddPair(const Pair: TJSONPair): TJSONObject; overload;
    function AddPair(const Str: string; const Val: TJSONValue): TJSONObject; overload;
    function AddPair(const Str: string; const Val: string): TJSONObject; overload;

    function ToJSON: string;
    function ToString: string; override;
  end;

  TJSONString = class(TJSONValue)
  private
    FValue: string;
  protected
    function GetStrValue: string; override;
    procedure SetStrValue(const Value: string); override;
  public
    constructor Create(AString: string); overload;
  end;

  TJSONNumber = class(TJSONString)
  private
    FInt: integer;
    FDouble: Double;
  protected
    function GetStrValue: string; override;
    procedure SetStrValue(const Value: string); override;
  public
    constructor Create(ANumber: integer); overload;
    constructor Create(ANumber: double); overload;

    property AsInt: integer read FInt write FInt;
    property AsDouble: Double read FDouble write FDouble;
    property AsFloat: Double read FDouble write FDouble;
  end;

  TJSONTrue = class(TJSONValue)
  protected
    function GetStrValue: string; override;
  public
    function ToString: string; override;
  end;

  TJSONFalse = class(TJSONValue)
  protected
    function GetStrValue: string; override;
  public
    function ToString: string; override;
  end;

  TJSONNull = class(TJSONValue)
  protected
    function GetStrValue: string; override;
  public
    function ToString: string; override;
  end;

  TJSONArray = class(TJSONObject)
  private
    fja: TJSArray;
    function GetItem(index: integer): TJSONValue;
    procedure SetItem(index: integer; const Value: TJSONValue);
    function GetCount: integer; reintroduce;
    function GetJA: TJSArray;
  protected
    property ja: TJSArray read GetJA write fja;
  public
    constructor Create(AArray: TJSArray); overload;
    property Count: integer read GetCount;
    property Items[index: integer]: TJSONValue read GetItem write SetItem;
    procedure Add(const Element: string); overload;
    procedure Add(const Element: boolean); overload;
    procedure Add(const Element: integer); overload;
    procedure Add(const Element: double); overload;
    procedure Add(const Element: TJSONObject); overload;
    procedure Add(const Element: TJSONPair); overload;

    function ToString: string; override;
  end;

  TJSON = class(TObject)
    function Parse(s: string): TJSONObject;
  end;


implementation


function JSONObjectToString(const v: JSValue): string; assembler;
asm
  return v+"";
end;

{ TJSON }

function TJSON.Parse(s: string): TJSONObject;
var
  O: TJSObject;
begin
  O := TJSJSON.parseObject(s);

  if isArray(O) then
  begin
    Result := TJSONArray.Create(TJSArray(O));
  end
  else
  begin
    Result := TJSONObject.Create(O);
  end;
end;

{ TJSONArray }

procedure TJSONArray.Add(const Element: string);
begin
  ja.push(Element);
end;

procedure TJSONArray.Add(const Element: integer);
begin
  ja.push(Element);
end;

procedure TJSONArray.Add(const Element: boolean);
begin
  ja.push(Element);
end;

procedure TJSONArray.Add(const Element: TJSONObject);
begin
  ja.push(Element);
end;

procedure TJSONArray.Add(const Element: double);
begin
  ja.push(Element);
end;

procedure TJSONArray.Add(const Element: TJSONPair);
begin
  ja.push(Element);
end;

constructor TJSONArray.Create(AArray: TJSArray);
begin
  inherited Create;
  ja := AArray;
end;

function TJSONArray.GetCount: integer;
begin
  Result := ja.Length;
end;

function TJSONArray.GetItem(index: integer): TJSONValue;
var
  jv: JSValue;
begin
  jv := ja[Index];
  if (jv is TJSONObject) or (jv is TJSONArray) then
    Result := TJSONValue(jv)
  else
    Result := JSONValueFromJS(jv);
end;

function TJSONArray.GetJA: TJSArray;
begin
  if not Assigned(fja) then
    fja := TJSArray.new;

  Result := fja;
end;

procedure TJSONArray.SetItem(index: integer; const Value: TJSONValue);
begin
  if Value is TJSONObject then
    ja[index] := TJSONObject(Value).jo;
end;

function TJSONArray.ToString: string;
var
  i: integer;
  jv: TJSONValue;
  comma: string;
begin
  Result := '[';
  comma := '';

  for i := 0 to Count - 1 do
  begin
    jv := Items[i];

    if Assigned(jv) then
    begin
      if (jv is TJSONObject) then
      begin
        Result := Result + comma + (jv as TJSONObject).ToString;
      end
      else
        Result := Result + comma + jv.ToString;

      comma := ',';
    end;
  end;

  Result := Result + ']';
end;

{ TJSONObject }

constructor TJSONObject.Create;
begin
  inherited Create;
  FMembers := TJSONPairList.Create;
  fjo := nil;
end;

function TJSONObject.AddPair(const Pair: TJSONPair): TJSONObject;
var
  LObj: TJSONObject;
begin
  if Assigned(Pair) then
  begin
    AddDescendant(Pair);
    LObj := TJSONObject(ParseJSONValue(ToJSON));
  end;
  Result := LObj;
end;

function TJSONObject.AddPair(const Str: string;
  const Val: TJSONValue): TJSONObject;
begin
  AddPair(TJSONPair.Create(Str, Val));
  Result := Self;
end;

procedure TJSONObject.AddDescendant(const Descendent: TJSONPair);
begin
  FMembers.Add(Descendent);
end;

function TJSONObject.AddPair(const Str, Val: string): TJSONObject;
begin
  AddPair(TJSONPair.Create(Str, Val));
  Result := Self;
end;

constructor TJSONObject.Create(AObject: TJSObject);
begin
  inherited Create;
  FMembers := TJSONPairList.Create;
  jo := AObject;
end;

destructor TJSONObject.Destroy;
begin
  FMembers.Free;
  inherited Destroy;
end;

function TJSONObject.Get(Index: Integer): TJSONPair;
begin
  Result := Get(TJSObject.keys(jo)[Index]);
end;

function TJSONObject.Get(Name: string): TJSONPair;
var
  jv: JSValue;
  jsv: TJSONValue;
begin
  Result := nil;
  jv :=  jo.Properties[Name];

  if Assigned(jv) then
  begin
    Result := TJSONPair.Create;
    jsv := JSONValueFromJS(jv);
    Result.JsonString.Value := Name;
    Result.JsonValue := jsv;
  end;
end;

function TJSONObject.GetCount: Integer;
begin
  Result := Length(TJSObject.keys(jo));
end;

function TJSONObject.GetItem(const Index: Integer): TJSONValue;
var
  jv: JSValue;
begin
  jv := jo[TJSObject.keys(jo)[Index]];
  Result := JSONValueFromJS(jv);
end;

function TJSONObject.GetJSONValue(Name: string): string;
var
  jv: JSValue;
begin
  jv :=  jo.Properties[Name];
  Result := JSONObjectToString(jv);
end;

function TJSONObject.GetPair(const I: Integer): TJSONPair;
begin
  if (I >= 0) and (I < FMembers.Count) then
    Result := TJSONPair(FMembers[I])
  else
    Result := nil;
end;

function TJSONObject.GetValue(const Name: string): TJSONValue;
var
  jp: TJSONPair;
begin
  Result := nil;

  jp := Get(Name);
  if Assigned(jp) then
    Result := jp.JsonValue;
end;

function TJSONObject.JSONValueFromJS(const AJSValue: JSValue): TJSONValue;
begin

  Result := nil;
  if isArray(AJSValue) then
  begin
    Result := TJSONArray.Create;
    (Result as TJSONArray).ja := TJSArray(AJSValue);
  end
  else
  if isObject(AJSValue) then
  begin
    Result := TJSONObject.Create;
    (Result as TJSONObject).jo := TJSObject(AJSValue);
  end
  else
  if isString(AJSValue) then
  begin
    Result := TJSONString.Create;
    (Result as TJSONString).Value := string(AJSValue);
  end
  else
  if isNumber(AJSValue) then
  begin
    Result := TJSONNumber.Create;
    (Result as TJSONNumber).Value := string(AJSValue);
    (Result as TJSONNumber).AsInt := integer(AJSValue);
    (Result as TJSONNumber).AsDouble := double(AJSValue);
  end
  else
  if isBoolean(AJSValue) then
  begin
    if toBoolean(AJSValue) then
      Result := TJSONTrue.Create
    else
      Result := TJSONFalse.Create;
  end;

  if Assigned(Result) then
    Result.jv := AJSValue;
end;

class function TJSONObject.ParseJSONValue(const data: string): TJSONValue;
var
  O: TJSObject;
begin
  O := TJSJSON.parseObject(data);

  if isArray(O) then
  begin
    Result := TJSONArray.Create;
    (Result as TJSONArray).ja := TJSArray(O);
  end
  else if isNull(O) then
    Result := TJSONNull.Create
  else
  begin
    Result := TJSONObject.Create;
    (Result as TJSONObject).jo := O;
  end;
end;

function TJSONObject.ToJSON: string;
var
  s: string;
  Size: Integer;
  I: Integer;
  jp: TJSONPair;
begin
  if Assigned(jo) then
    Size := Count
  else
    Size := FMembers.Count;

  if (Self is TJSONArray) then
  begin
    Result := (Self as TJSONArray).ToString;
  end
  else
  begin
    s := '{';
    if Size > 0 then
    begin
      if Assigned(jo) then
      begin
        jp := TJSONPair(Get(0));
        if Assigned(jp) then
          s := s + jp.ToString
      end
      else
        s := s + TJSONPair(FMembers[0]).ToString;
    end;

    for I := 1 to Size - 1 do
    begin
      s := s + ',';
      if Assigned(jo) then
      begin
        jp := TJSONPair(Get(I));
        if Assigned(jp) then
          s := s + jp.ToString;
      end
      else
        s := s + TJSONPair(FMembers[I]).ToString;
    end;
    s := s + '}';
    Result := s;
  end;
end;

function TJSONObject.ToString: string;
begin
  Result := ToJSON;
end;

{ TJSONPair }

constructor TJSONPair.Create;
begin
  inherited;
  fjs := TJSONString.Create;
end;

constructor TJSONPair.Create(const Str: string; const Value: TJSONValue);
begin
  inherited Create;
  JsonString := TJSONString.Create(Str);
  JsonValue := Value;
end;

constructor TJSONPair.Create(const Str, Value: string);
begin
  inherited Create;
  JsonString := TJSONString.Create(Str);
  JsonValue := TJSONString.Create(Value);
end;

destructor TJSONPair.Destroy;
begin
  fjs.Free;
  inherited;
end;

function TJSONPair.ToString: string;
begin
  if (JsonValue is TJSONObject) then
    Result := '"' + JsonString.GetStrValue + '":' + (JsonValue as TJSONObject).ToJSON
  else
  begin
    if (JsonString <> nil) and (JsonValue <> nil) then
      Result := '"' + JsonString.GetStrValue + '":' + JsonValue.ToString
    else
      Result := '';
  end;
end;

{ TJSONAncestor }

function TJSONAncestor.GetStrValue: string;
begin
  Result := '';
end;

procedure TJSONAncestor.SetStrValue(const Value: string);
begin
  //
end;

{ TJSONString }

constructor TJSONString.Create(AString: string);
begin
  inherited Create;
  jv := AString;
  FValue := AString;
end;

function TJSONString.GetStrValue: string;
begin
  Result := FValue;
end;

procedure TJSONString.SetStrValue(const Value: string);
begin
  FValue := Value;
end;

{ TJSONNumber }

constructor TJSONNumber.Create(ANumber: integer);
begin
  inherited Create;
  jv := ANumber;
end;

constructor TJSONNumber.Create(ANumber: double);
begin
  inherited Create;
  jv := ANumber;
end;

function TJSONNumber.GetStrValue: string;
begin
  Result := FloatToStr(FDouble);
end;

procedure TJSONNumber.SetStrValue(const Value: string);
begin
  FInt := StrToInt(Value);
  FDouble := StrToFloat(Value);
end;

{ TJSONTrue }

function TJSONTrue.GetStrValue: string;
begin
   Result := 'true';
end;

function TJSONTrue.ToString: string;
begin
  Result := GetStrValue;
end;

{ TJSONFalse }

function TJSONFalse.GetStrValue: string;
begin
  Result := 'false';
end;


function TJSONFalse.ToString: string;
begin
  Result := GetStrValue;
end;

{ TJSONNull }

function TJSONNull.GetStrValue: string;
begin
  Result := 'null';
end;


function TJSONNull.ToString: string;
begin
  Result := GetStrValue;
end;

{ TJSONValue }

constructor TJSONValue.Create(AJSValue: JSValue);
begin
  inherited Create;
  jv := AJSValue;
end;


function TJSONValue.ToString: string;
begin
  Result := TJSJSON.stringify(jv);
end;

end.
